import {
  Dialog as MuiDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Button from "../Button/Button";
import { useDesignTokens } from "../../../theme/useDesignTokens";

/**
 * Common Dialog Component
 * Provides consistent dialog styling and behavior
 */
const Dialog = ({
  open = false,
  onClose,
  title,
  children,
  actions = [],
  maxWidth = "sm",
  fullWidth = true,
  sx = {},
  disablePortal = false,
  scroll = "paper",
  loading = false,
  PaperProps = {},
  fullScreen = false,
  showCloseButton = false,
  closeOnBackdropClick = false,
  closeOnEscapeKeyDown = false,
  titleSx = {},
  contentSx = {},
}) => {
  const { font } = useDesignTokens();

  return (
    <MuiDialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" && !closeOnBackdropClick) {
          return;
        }
        if (reason === "escapeKeyDown" && !closeOnEscapeKeyDown) {
          return;
        }
        if (typeof onClose === "function") {
          onClose(event, reason);
        }
      }}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={fullScreen}
      sx={sx}
      disablePortal={disablePortal}
      scroll={scroll}
      slotProps={{
        transition: { timeout: 300 },
        backdrop: {
          transition: { timeout: 300 },
        },
      }}
      PaperProps={PaperProps}
    >
      {title && (
        <DialogTitle
          sx={{
            fontWeight: font?.weight?.fontW700,
            fontSize: font?.size?.fontSz20,
            position: "relative",
            pr: showCloseButton ? 7 : undefined,
            ...titleSx,
          }}
        >
          {title}
          {showCloseButton && (
            <IconButton
              aria-label="Close"
              onClick={(event) => {
                if (typeof onClose === "function") {
                  onClose(event, "closeButtonClick");
                }
              }}
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
      )}
      {children && (
        <DialogContent sx={{ pt: title ? 2 : 3, position: "relative", ...contentSx }}>
          {!title && showCloseButton && (
            <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
              <IconButton
                aria-label="Close"
                onClick={(event) => {
                  if (typeof onClose === "function") {
                    onClose(event, "closeButtonClick");
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          {children}
        </DialogContent>
      )}
      {!loading && actions && actions.length > 0 && (
        <DialogActions sx={{ padding: "16px 24px", gap: 1 }}>
          {actions.map((action, index) => (
            <Button
              // key={index}
              key={action.key ?? `${action.label ?? "action"}-${index}`}
              variant={action.variant || "primary"}
              size={action.size || "medium"}
              onClick={action.onClick}
              disabled={action.disabled}
              startIcon={action.startIcon}
              endIcon={action.endIcon}
              loading={action.loading}
              isNotAllowed={action?.isNotAllowed}
              tooltip={action.tooltip}
              slotProps={action.slotProps}
            >
              {action.label}
            </Button>
          ))}
        </DialogActions>
      )}
    </MuiDialog>
  );
};

export default Dialog;
